import { useMemo } from "react";
import { ListFilter } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { AppIcon } from "@/v2/components/EntryCard";
import type { Entry } from "@/shared/types/entries";
import type { Matter } from "@/shared/cache/slices/matters";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import { isTaskRequired, isActivityRequired } from "@/shared/utils/firm";
import { detectRestrictedWords } from "@/shared/utils/restrictedWords";
import { FILTERABLE_APPLICATIONS } from "@/features/attorney/hooks/useApplicationFilter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StatusFilter = "complete" | "incomplete" | "needs-review";

export type FilterState = {
	hiddenApps: Set<string>;
	statuses: Set<StatusFilter>;
};

export const DEFAULT_FILTER: FilterState = {
	hiddenApps: new Set(),
	statuses: new Set<StatusFilter>(["complete", "incomplete", "needs-review"]),
};

// Apps that should never be filtered out
const ALWAYS_VISIBLE_APPS = new Set(["Combine", "Duplicate", "User Input", ""]);

export function isFilterActive(filter: FilterState): boolean {
	return (
		filter.hiddenApps.size > 0 ||
		filter.statuses.size < DEFAULT_FILTER.statuses.size
	);
}

// ---------------------------------------------------------------------------
// Entry status classification
// ---------------------------------------------------------------------------

export type EntryStatus = "complete" | "incomplete" | "needs-review";

export function classifyEntry(
	entry: Entry,
	matters?: Matter[],
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[],
): EntryStatus {
	// Needs attention: admin feedback, needs_improvement, or restricted words in narrative
	const hasAdminIssue = !!(entry.needs_improvement || entry.attorney_feedback);

	let hasRestrictedWords = false;
	if (getRestrictedWords && entry.description) {
		const clientCode = entry.matter_id?.split(".")[0];
		const matterCode = entry.matter_id?.split(".").slice(1).join(".");
		const words = getRestrictedWords(clientCode, matterCode);
		if (words.length > 0) {
			hasRestrictedWords = detectRestrictedWords(entry.description, words).length > 0;
		}
	}

	if (hasAdminIssue || hasRestrictedWords) return "needs-review";

	// Incomplete: missing required fields
	const taskRequired = matters ? isTaskRequired(entry.matter_id, matters) : false;
	const activityRequired = matters ? isActivityRequired(entry.matter_id, matters) : false;

	const isIncomplete =
		!entry.matter ||
		!entry.description ||
		entry.duration === 0 ||
		(taskRequired && !entry.task) ||
		(activityRequired && !entry.activity) ||
		entry.description === "Please fill in the narrative.";

	if (isIncomplete) return "incomplete";

	return "complete";
}

// ---------------------------------------------------------------------------
// Filter application
// ---------------------------------------------------------------------------

export function applyFilters(
	entries: Entry[],
	filter: FilterState,
	matters?: Matter[],
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[],
): Entry[] {
	return entries.filter((e) => {
		// App filter
		if (
			e.application &&
			!ALWAYS_VISIBLE_APPS.has(e.application) &&
			filter.hiddenApps.has(e.application)
		) {
			return false;
		}

		// Status filter
		const status = classifyEntry(e, matters, getRestrictedWords);
		if (!filter.statuses.has(status)) return false;

		return true;
	});
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface FilterPopoverProps {
	filter: FilterState;
	onChange: (filter: FilterState) => void;
	entries: Entry[];
	matters?: Matter[];
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
}

export function FilterPopover({ filter, onChange, entries, matters, getRestrictedWords }: FilterPopoverProps) {
	const active = isFilterActive(filter);

	const applications = useMemo(() => {
		// Count entries per app name from current view
		const counts = new Map<string, number>();
		for (const e of entries) {
			const app = e.application || "";
			if (ALWAYS_VISIBLE_APPS.has(app)) continue;
			counts.set(app, (counts.get(app) || 0) + 1);
		}

		// Always show the full known app list, with live counts
		const knownNames = new Set<string>(FILTERABLE_APPLICATIONS.map((a) => a.applicationName));
		const known = FILTERABLE_APPLICATIONS.map(({ applicationName, displayName }) => ({
			name: applicationName,
			displayName,
			count: counts.get(applicationName) || 0,
		}));

		// Append any apps in the entries that aren't in the known list
		const extras = [...counts.entries()]
			.filter(([name]) => !knownNames.has(name))
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([name, count]) => ({ name, displayName: name, count }));

		return [...known, ...extras];
	}, [entries]);

	// Count entries per status
	const statusCounts = useMemo(() => {
		const counts = { complete: 0, incomplete: 0, "needs-review": 0 };
		for (const e of entries) {
			const status = classifyEntry(e, matters, getRestrictedWords);
			counts[status]++;
		}
		return counts;
	}, [entries, matters, getRestrictedWords]);

	const toggleApp = (app: string) => {
		const next = new Set(filter.hiddenApps);
		if (next.has(app)) next.delete(app);
		else next.add(app);
		onChange({ ...filter, hiddenApps: next });
	};

	const showAllApps = () => onChange({ ...filter, hiddenApps: new Set() });
	const hideAllApps = () =>
		onChange({ ...filter, hiddenApps: new Set(applications.map((a) => a.name)) });

	const toggleStatus = (status: StatusFilter) => {
		const next = new Set(filter.statuses);
		if (next.has(status)) next.delete(status);
		else next.add(status);
		onChange({ ...filter, statuses: next });
	};

	const clearAll = () => onChange({ ...DEFAULT_FILTER });

	const statusItems: { id: StatusFilter; label: string; count: number }[] = [
		{ id: "complete", label: "Ready", count: statusCounts.complete },
		{ id: "incomplete", label: "Incomplete", count: statusCounts.incomplete },
		{ id: "needs-review", label: "Needs attention", count: statusCounts["needs-review"] },
	];

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-10 w-10 text-muted-foreground"
				>
					<ListFilter className="size-5" />
					{active && (
						<span className="absolute top-1 right-1 size-1.5 rounded-full bg-primary" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-64 p-0" align="start">
				{/* Application section */}
				<div className="flex items-center justify-between px-3 pt-3 pb-1.5">
					<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Application</span>
					<div className="flex items-center gap-1.5">
						<button
							type="button"
							onClick={showAllApps}
							className="text-xs text-primary hover:underline"
						>
							All
						</button>
						<span className="text-xs text-muted-foreground">/</span>
						<button
							type="button"
							onClick={hideAllApps}
							className="text-xs text-primary hover:underline"
						>
							None
						</button>
					</div>
				</div>
				<div className="px-1.5 pb-1.5 max-h-[200px] overflow-y-auto">
					{applications.map(({ name, displayName, count }) => (
						<label
							key={name}
							className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-accent cursor-pointer"
						>
							<Checkbox
								checked={!filter.hiddenApps.has(name)}
								onCheckedChange={() => toggleApp(name)}
							/>
							<span className="shrink-0 size-5 flex items-center justify-center [&_svg]:size-5 [&_img]:size-5">
								<AppIcon application={name} />
							</span>
							<span className="text-sm flex-1 truncate">{displayName}</span>
							<span className="text-xs text-muted-foreground tabular-nums">{count || ""}</span>
						</label>
					))}
				</div>

				{/* Status section */}
				<div className="border-t border-border">
					<div className="px-3 pt-2.5 pb-1.5">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</span>
					</div>
					<div className="px-1.5 pb-1.5">
						{statusItems.map(({ id, label, count }) => (
							<label
								key={id}
								className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-accent cursor-pointer"
							>
								<Checkbox
									checked={filter.statuses.has(id)}
									onCheckedChange={() => toggleStatus(id)}
								/>
								<span className="text-sm flex-1">{label}</span>
								<span className="text-xs text-muted-foreground tabular-nums">{count}</span>
							</label>
						))}
					</div>
				</div>

				{/* Clear all */}
				{active && (
					<div className="border-t border-border p-1.5">
						<Button
							variant="ghost"
							size="sm"
							onClick={clearAll}
							className="w-full text-xs h-7 text-muted-foreground hover:text-foreground"
						>
							Clear filters
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
